// deps
import React from "react";
import PropTypes from "prop-types";
import Head from "next/head";
import { useIntl } from "react-intl";

// constants
import {
  COMMENT_MIN_RATE,
  COMMENT_MAX_RATE,
} from "@cloudspire/legacy-resources/src/constants/comment";

// helpers
import { seoGetContentRelayOnRoom } from "@cloudspire/legacy-resources/src/helpers/seo";
import {
  roomHasPictures,
  roomIsNatureParent,
} from "@cloudspire/legacy-resources/src/helpers/room";
import bookingDelegationGetChannelId from "@cloudspire/legacy-resources/src/helpers/booking/delegation/getChannelId";

// libraries
import { generateUri } from "@cloudspire/legacy-shared/src/libraries";
import intlFormatConstantByValue from "@cloudspire/shared/utils/intl/formatConstantByValue";

// contexts
import { useConfiguration } from "@cloudspire/legacy-resources/src/contexts/configuration";
import { useRouters } from "@cloudspire/legacy-resources/src/contexts/routers";
import { useRoute } from "@cloudspire/legacy-resources/src/contexts/route";

// api
import { roomRoomPage } from "@cloudspire/legacy-resources/src/api/room";
import { pricingEstimateRoomPage } from "@cloudspire/legacy-resources/src/api/pricing";
import { commentsRoomPage } from "@cloudspire/legacy-resources/src/api/comment";

// hooks
import { useResponse } from "@cloudspire/legacy-resources/src/hooks/fetcher";

// constants
import { searchDefaultParameters } from "../../constants/search";
import { ROOM_COMMENT_PER_PAGE } from "../../constants/pages";
import { HOTEL_TYPE_INTL_MESSAGES } from "@cloudspire/shared/constants/hotel";

function RoomSEO(props) {
  const { roomReference, roomSlug } = props;

  const configuration = useConfiguration();

  const roomResponse = useResponse(
    roomRoomPage({
      referenceOrSlug: roomSlug,
      configuration,
    })
  );

  const room = roomResponse.data?.data;

  const roomReferenceList = room ? [roomReference] : null;
  if (roomIsNatureParent({ room: room })) {
    for (const roomChild of room?.children ?? []) {
      roomReferenceList.push(roomChild.reference);
    }
  }

  const pricingEstimatesResponse = useResponse(
    pricingEstimateRoomPage({
      roomReferenceList,
      channelId: bookingDelegationGetChannelId({ configuration, room }),
    })
  );

  const pricing = pricingEstimatesResponse.data?.data;

  const commentsResponse = useResponse(
    commentsRoomPage({
      roomReference,
      query: {
        ...searchDefaultParameters,
        per_page: ROOM_COMMENT_PER_PAGE,
        page: 1,
      },
    })
  );

  const comments = commentsResponse.data?.data;

  const intl = useIntl();

  const { frontRouter } = useRouters();

  const route = useRoute();

  const coverUri = room?.pictureList?.[0]?.uri;

  const seoInformation = [];

  if (room) {
    seoInformation.push(
      ...seoGetContentRelayOnRoom({
        configuration,
        room,
        intl,
      })
    );

    if (coverUri) {
      seoInformation.push(
        <meta key="ogImage" property="og:image" content={coverUri} />
      );
      seoInformation.push(
        <meta key="twitterImage" name="twitter:image" content={coverUri} />
      );
    }

    const url = generateUri({
      router: frontRouter,
      name: route.getName(),
      parameters: {
        lang: intl.locale,
        slug: room.slug,
      },
      scheme: "https",
      host: configuration.channel.website.domain,
    });

    seoInformation.push(
      <script
        key="json-ld"
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "http://schema.org/",
            "@type": "Product",
            mpn: room.reference,
            sku: room.reference,
            name: room.name,
            brand: configuration.channel.title,
            image: roomHasPictures({ room }) ? room.pictureList[0].uri : null,
            description: room.about,
            ...(comments && {
              aggregateRating: {
                "@type": "AggregateRating",
                ratingValue: room.rate,
                reviewCount: room.reviews,
                worstRating: COMMENT_MIN_RATE,
                bestRating: COMMENT_MAX_RATE,
              },
            }),
            url,
            category: intlFormatConstantByValue(
              {
                intl,
                messages: HOTEL_TYPE_INTL_MESSAGES.byValue,
                value: room.type,
                preferredAttributes: ["label2", "label"],
              },
              {
                uppercaseFirst: true,
              }
            ),
            ...(pricing && {
              offers: {
                "@type": "Offer",
                priceCurrency: "EUR",
                price: pricing?.[room?.reference]?.amount,
                url,
                availability: "http://schema.org/InStock",
                seller: {
                  "@type": "Organization",
                  name: configuration.channel.title,
                },
              },
            }),
            ...(comments && {
              review: comments.map((comment) => ({
                "@type": "Review",
                author: comment?.customerName ?? "n/a",
                datePublished: comment.answeredAt,
                description: comment.customerContent,
                reviewRating: {
                  "@type": "Rating",
                  ratingValue: comment.averageRate,
                  bestRating: COMMENT_MAX_RATE,
                  worstRating: COMMENT_MIN_RATE,
                },
              })),
            }),
          }),
        }}
      />
    );
  }

  return <Head>{seoInformation}</Head>;
}

RoomSEO.propTypes = {
  roomSlug: PropTypes.string,
  roomReference: PropTypes.string,
};

export default RoomSEO;
